import { useEffect, useMemo, useState } from 'react';
import pericopesData from '../assets/bible/pericopes.json';

/**
 * Découpage du texte biblique en péricopes — les unités de sens.
 *
 * Deux jeux de données, comme côté Flutter :
 *
 * - `pericopes.json` — les positions, communes à toutes les langues, 21 Ko :
 *   importées statiquement, elles sont toujours nécessaires.
 * - `<lang>/pericope_titles.json` — les intitulés, ~100 Ko **par langue** :
 *   chargés à la demande, sinon les sept langues entreraient toutes dans le
 *   bundle pour que l'une d'elles serve.
 *
 * Les deux se dégradent séparément et sans bruit : un livre absent des positions
 * n'a pas de péricope (le chapitre s'affiche comme avant), un intitulé absent ou
 * non traduit laisse la rupture s'exprimer par la seule typographie.
 *
 * Les intitulés viennent de bible-center.ru, via codexfidei. La donnée est
 * dupliquée à l'identique dans les deux arbres d'assets : l'app et le site
 * doivent découper le même texte au même endroit.
 */

type Positions = Record<string, [number, number][]>;
type Titres = Record<string, string[]>;

const positions = pericopesData as unknown as Positions;

/**
 * Langues dont on embarque les intitulés. La liste est explicite parce que
 * l'import dynamique ci-dessous doit rester statiquement analysable : webpack
 * fabrique un chunk par fichier correspondant, et ne charge que celui demandé.
 */
const LANGUES_AVEC_TITRES = ['fr', 'en', 'es', 'it', 'de', 'ru', 'el'];

/** "jn" → "Jn", "1jn" → "1Jn" — même normalisation que useCommentaryEntries. */
const normalizeBookId = (id: string): string => {
  if (!id) return id;
  if (/^\d/.test(id)) return id[0] + id[1].toUpperCase() + id.slice(2).toLowerCase();
  return id[0].toUpperCase() + id.slice(1).toLowerCase();
};

const titresEnMemoire = new Map<string, Titres>();

const chargerTitres = async (lang: string): Promise<Titres> => {
  const dejaLa = titresEnMemoire.get(lang);
  if (dejaLa) return dejaLa;

  if (!LANGUES_AVEC_TITRES.includes(lang)) {
    // Langue sans intitulés traduits : la typographie parlera seule.
    titresEnMemoire.set(lang, {});
    return {};
  }

  try {
    const module = await import(`../assets/bible/${lang}/pericope_titles.json`);
    const titres = (module.default ?? module) as Titres;
    titresEnMemoire.set(lang, titres);
    return titres;
  } catch {
    // Chunk absent ou illisible : le lecteur doit continuer de fonctionner.
    titresEnMemoire.set(lang, {});
    return {};
  }
};

/**
 * Péricopes ouvertes dans ce chapitre : verset de départ → intitulé.
 *
 * L'intitulé vaut `''` quand il est inconnu dans cette langue. Le verset 1 n'y
 * figure pas systématiquement : 15 % des chapitres poursuivent l'unité ouverte
 * au chapitre précédent, et il ne faut pas y marquer une rupture que le texte
 * ne fait pas.
 */
export const getPericopeStarts = (
  bookId: string | undefined,
  chapter: string | number | undefined,
  titres?: Titres
): Map<number, string> => {
  const vide = new Map<number, string>();
  if (!bookId || chapter === undefined || chapter === null) return vide;

  const chapterNum = typeof chapter === 'number' ? chapter : parseInt(chapter, 10);
  if (!Number.isFinite(chapterNum)) return vide;

  const livre = normalizeBookId(bookId);
  const debuts = positions[livre];
  if (!debuts) return vide;

  // Le tableau des titres est parallèle à celui des positions : c'est le rang
  // dans le livre qui les relie.
  const titresDuLivre = titres?.[livre];
  const resultat = new Map<number, string>();
  debuts.forEach(([ch, v], rang) => {
    if (ch !== chapterNum) return;
    resultat.set(v, titresDuLivre?.[rang] ?? '');
  });
  return resultat;
};

export const usePericopeStarts = (
  bookId: string | undefined,
  chapter: string | number | undefined,
  lang: string | undefined
): Map<number, string> => {
  // i18n sert parfois « fr-FR » : les assets sont rangés par code à deux lettres.
  const code = lang ? lang.split('-')[0].toLowerCase() : undefined;

  const [titres, setTitres] = useState<Titres | undefined>(
    code ? titresEnMemoire.get(code) : undefined
  );

  useEffect(() => {
    if (!code) return;
    let annule = false;
    chargerTitres(code).then(t => {
      if (!annule) setTitres(t);
    });
    return () => {
      annule = true;
    };
  }, [code]);

  return useMemo(
    () => getPericopeStarts(bookId, chapter, titres),
    [bookId, chapter, titres]
  );
};
